import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { User } from '@mirage-projects/cw-models';

import { AclConfig } from './acl-config';
import { ACL, AclString } from './acl.enum';
import { UserAcl } from './user-acl';

export const AUTHORIZATION_ADMIN_ROLE = 'Admin';

/**
 * This service implements the business logic necessary for the application's ACL control.
 */
@Injectable({
    providedIn: 'root',
})
export class AclControlService {
    // Config ACLs for app
    private _aclConfig: AclConfig[] = [];
    // ACLs permissions for the logged user
    private _userAcls: UserAcl[] = [];
    private _user?: User;

    constructor(
        private http: HttpClient,
        private location: Location
    ) {}

    /* Run at application startup to get acls configuration */
    initAclConfig(): Observable<AclConfig[]> {
        const urlAclConfig = this.location.prepareExternalUrl('assets/acls/acls.json');
        return this.http.get<AclConfig[]>(urlAclConfig).pipe(
            switchMap((data) => {
                this.aclConfig = data;
                return of(data);
            })
        );
    }

    /* transform enum to array */
    get arrayAclConfig(): string[] {
        return Object.keys(ACL);
    }

    get aclConfig(): AclConfig[] {
        return this._aclConfig;
    }

    set aclConfig(aclConfig: AclConfig[]) {
        this._aclConfig = aclConfig;
    }

    get userAcls(): UserAcl[] {
        return this._userAcls;
    }

    set userAcls(userAcls: UserAcl[]) {
        this._userAcls = userAcls;
    }

    get user(): User | undefined {
        return this._user;
    }

    set user(user: User | undefined) {
        this._user = user;
    }

    /*
    Initialize all acls to false
    */
    resetAcls(): void {
        this.arrayAclConfig.forEach((acl) => {
            this.userAcls.push(new UserAcl(acl, false));
        });
    }

    /*
    Load ACLs permissions into propertie userAcls
    */
    loadUserACLs(user: User): void {
        this.user = user;
        this.resetAcls();
        this.aclConfig.forEach((aclConf) => {
            // All user acls with permission are in the allAcls property. They all have the effect property 'ALLOW'.
            const existAclInUser = this.user?.allAcls?.some((aclUser) => aclUser.uid === aclConf.uid);
            if (existAclInUser) {
                this.setUserAcl(aclConf.name, true);
            }
        });
    }

    /*
        Return true if user has ACL active
    */
    hasUserACL(acl: AclString): boolean {
        if (this.user?.role === AUTHORIZATION_ADMIN_ROLE) {
            return true;
        } else {
            const findAcl = this.userAcls.find((userAcl) => userAcl.acl === acl);
            return findAcl ? findAcl.active : false;
        }
    }

    /*
        Return true if user has Enable All ACL active
    */
    hasEnableAllACL(): boolean {
        const findAcl = this.userAcls.find((userAcl) => userAcl.acl === ACL.ENABLE_ALL);
        return findAcl ? findAcl.active : false;
    }

    /*
        Return true if the user has any Acl from those passed as parameters
    */
    hasUserAnyACL(acls: AclString[]): boolean {
        if (!acls.length) {
            return false;
        }
        return acls.some((acl) => this.hasUserACL(acl));
    }

    /**
     * Checks if the user has permission to edit the object permission
     */
    hasUserPermissionsToEditObjectPermissions(): boolean {
        return this.hasUserAnyACL([
            ACL.USER_ADD_OBJECT_PERMISSION,
            ACL.USERS_REMOVE_OBJECT_PERMISSION,
            ACL.GROUPS_ADD_OBJECT_PERMISSION,
            ACL.GROUPS_REMOVE_OBJECT_PERMISSION,
        ]);
    }

    /**
     * Check if the user has access to technical preview features
     */
    hasUserTechnicalPreviewACL(): boolean {
        return this.userAcls.some((userAcl) => userAcl.acl === ACL.TECHNICAL_PREVIEW && userAcl.active);
    }

    /**
     *  If ACL exists: Set the active.
     *  If ACL not exists: Add the ACL
     */
    private setUserAcl(acl: AclString, active: boolean): void {
        const aclFind = this.userAcls.find((userAcl) => userAcl.acl === acl);
        if (aclFind) {
            aclFind.active = active;
        } else {
            this.userAcls.push(new UserAcl(acl, active));
        }
    }
}
